import React, { memo } from "react";

import { formatCurrency, transformDateToLocalDateString } from "helpers";
import { VEHICLE_LOCATION_TYPE_CONFIG } from "services/api/fleet/vehicles/locations/consts";
import { ShowcaseCard, ShowcaseIntro, ShowcaseNotice } from "ui/components/Showcase";

import { TransferIntroProps } from "./types";

/** Primjeri dok korisnik ništa nije upisao. */
const TRANSFER_SAMPLE = {
  from: "Poslovnica Zagreb",
  to: "Split, Poljička 12",
  name: "Isporuka kupcu",
  expenses: [
    { name: "Gorivo", amount: 45 },
    { name: "Cestarina", amount: 12.5 },
    { name: "Parking", amount: 8 },
  ],
};

const getStepText = (step: number, isEdit: boolean) => {
  if (step === 1) {
    return {
      title: "Kada i tko?",
      description: isEdit
        ? "Datumi susjednih premještaja poravnaju se uz promijenjeni datum."
        : "Upišite datum premještaja i vozača koji je vozilo odvezao.",
    };
  }
  if (step === 2) {
    return {
      title: "Troškovi premještaja",
      description: "Gorivo, cestarina ili parking. Troškovi se vežu na vozilo i vide se na tabu Troškovi.",
    };
  }
  return isEdit
    ? {
        title: "Ispravite zapis o premještaju",
        description: "Mijenja se postojeći premještaj, ne nastaje novi.",
      }
    : {
        title: "Premjestite vozilo",
        description: "Odaberite gdje se vozilo od sada nalazi. Prethodna lokacija ostaje zapisana u povijesti vozila.",
      };
};

/** Lijevi panel premještaja vozila: ruta od stare do nove lokacije, detalji premještaja i troškovi. */
const TransferIntro: React.FC<TransferIntroProps> = ({
  formValues,
  currentLocation,
  expenses,
  isEdit,
  stepCurrent,
  stepCount,
}) => {
  const text = getStepText(stepCurrent, isEdit);
  const typeConfig = VEHICLE_LOCATION_TYPE_CONFIG[formValues.locationType];
  const from = currentLocation?.locationName || TRANSFER_SAMPLE.from;
  const to =
    formValues.name ||
    [formValues.addressLine1, formValues.addressCity].filter(Boolean).join(", ") ||
    typeConfig?.label ||
    TRANSFER_SAMPLE.to;
  const date = transformDateToLocalDateString(formValues.validFrom ?? new Date());

  const expenseRows =
    expenses.length > 0 ? expenses.map((expense) => ({ name: expense.name, amount: expense.amount })) : TRANSFER_SAMPLE.expenses;
  const expenseTotal = expenseRows.reduce((total, expense) => total + (expense.amount ?? 0), 0);

  return (
    <ShowcaseIntro
      sceneKey={stepCurrent}
      title={text.title}
      description={text.description}
      stepCurrent={stepCurrent}
      stepCount={stepCount}
    >
      {stepCurrent === 0 && (
        <>
          <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Premještaj</div>

              {/* Ruta: stara lokacija gore, nova dolje, isprekidana crta između. */}
              <div className="mt-4 flex gap-3">
                <div className="flex flex-col items-center pt-1">
                  <span className="w-3 h-3 rounded-full border-2 border-slate-400 bg-white" />
                  <span className="flex-1 w-px my-1 border-l-2 border-dashed border-slate-300" />
                  <span className="w-3 h-3 rounded-full bg-blue-600 ring-4 ring-blue-100" />
                </div>
                <div className="flex-1 min-w-0 space-y-8">
                  <div>
                    <div className="text-[0.6rem] text-slate-400">Sada</div>
                    <div className="text-[0.75rem] font-semibold text-slate-500 truncate">{from}</div>
                  </div>
                  <div>
                    <div className="text-[0.6rem] text-blue-500 font-semibold">Novo</div>
                    <div className="text-sm font-bold text-slate-800 truncate">{to}</div>
                  </div>
                </div>
              </div>
              <div className="mt-6 rounded-md bg-slate-50 px-3 py-2 text-[0.65rem] text-slate-500">Od {date}</div>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Vrsta lokacije</div>
              <div className="mt-2 text-xs font-bold text-slate-800">{typeConfig?.label ?? "Poslovnica"}</div>
              <div className="mt-1 text-[0.65rem] leading-relaxed text-slate-400">{typeConfig?.description}</div>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
            <ShowcaseNotice
              title="Povijest ostaje zapisana"
              subtitle="Svaka lokacija sa svojim datumom"
              iconClassName="bg-blue-100"
              icon={
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#1D4ED8" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 21s7-7.75 7-13a7 7 0 00-14 0c0 5.25 7 13 7 13z" />
                  <circle cx="12" cy="8" r="2.5" />
                </svg>
              }
            />
          </ShowcaseCard>
        </>
      )}

      {stepCurrent === 1 && (
        <>
          <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Detalji premještaja</div>
              <div className="text-sm font-bold text-slate-800 truncate">{formValues.name || TRANSFER_SAMPLE.name}</div>
              <div className="mt-3 space-y-2">
                {[
                  { label: "Datum", value: date },
                  { label: "Odredište", value: to },
                  { label: "Vozač", value: formValues.driverId ? "Odabran" : "Nije odabran" },
                ].map((row) => (
                  <div key={row.label} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2">
                    <span className="text-[0.7rem] text-slate-500">{row.label}</span>
                    <span className="text-[0.7rem] font-semibold text-slate-800 truncate">{row.value}</span>
                  </div>
                ))}
              </div>
              {formValues.note && <p className="mt-3 text-[0.65rem] text-slate-500 line-clamp-2">{formValues.note}</p>}
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Povijest lokacija</div>
              <div className="mt-3 space-y-2 text-[0.65rem]">
                <div className="flex items-center gap-2 text-slate-400">
                  <span className="w-2 h-2 rounded-full bg-slate-300" />
                  <span className="truncate">{from}</span>
                </div>
                <div className="flex items-center gap-2 font-semibold text-slate-700">
                  <span className="w-2 h-2 rounded-full bg-blue-600" />
                  <span className="truncate">{to}</span>
                </div>
              </div>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
            <ShowcaseNotice
              title="Zna se tko je vozio"
              subtitle="Vozač se zapisuje uz premještaj"
              iconClassName="bg-amber-100"
              icon={
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#B45309" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M20 21v-2a4 4 0 00-4-4H8a4 4 0 00-4 4v2M12 11a4 4 0 100-8 4 4 0 000 8z" />
                </svg>
              }
            />
          </ShowcaseCard>
        </>
      )}

      {stepCurrent === 2 && (
        <>
          <ShowcaseCard className="left-[0.5rem] top-[-1rem] w-[17rem] h-[18rem]" delay={0}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Troškovi premještaja</div>
              <div className="text-sm font-bold text-slate-800 truncate">{formValues.name || TRANSFER_SAMPLE.name}</div>
              <div className="mt-3 space-y-2">
                {expenseRows.slice(0, 4).map((expense, index) => (
                  <div key={index} className="flex items-center justify-between gap-2 rounded-md bg-slate-50 px-3 py-2">
                    <span className="text-[0.7rem] font-medium text-slate-700 truncate">{expense.name}</span>
                    <span className="text-[0.7rem] font-semibold text-slate-800">{formatCurrency.format(expense.amount ?? 0)}</span>
                  </div>
                ))}
              </div>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="right-[-0.5rem] top-[6rem] w-[14rem]" delay={0.25}>
            <div className="p-5">
              <div className="text-[0.65rem] font-semibold uppercase tracking-wide text-slate-400">Ukupno</div>
              <div className="mt-2 text-xl font-bold text-slate-800">{formatCurrency.format(expenseTotal)}</div>
              <div className="mt-1 text-[0.65rem] text-slate-400">{expenseRows.length} troška</div>
            </div>
          </ShowcaseCard>

          <ShowcaseCard className="left-[-1.5rem] bottom-[-0.5rem] w-[15rem]" delay={0.5}>
            <ShowcaseNotice
              title="Troškovi idu na vozilo"
              subtitle="Vide se na tabu Troškovi"
              iconClassName="bg-emerald-100"
              icon={
                <svg className="w-4 h-4" viewBox="0 0 24 24" fill="none" stroke="#047857" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
                  <path d="M12 1v22M17 5H9.5a3.5 3.5 0 000 7h5a3.5 3.5 0 010 7H6" />
                </svg>
              }
            />
          </ShowcaseCard>
        </>
      )}
    </ShowcaseIntro>
  );
};

export default memo(TransferIntro);
